/**
 * Akte (27.10.2026) — der erste Baustein der Datenschicht.
 *
 * Eine Akte ist eine Folge **typisierter Einträge** über eine Person: Ärztin,
 * Impfung, Allergie, Medikament, Größe, Schule, Notfallkontakt,
 * Versicherungskarte, Ausweis. Jeder Eintrag ist eine Zeile in
 * `record_entries` mit `kind` und einer Karte `fields` — nicht eine Spalte je
 * Angabe. So kommt eine neue Art (später Tiere, Auto, Verträge) ohne
 * Migration dazu: hier eine Zeile in `RECORD_FIELDS`, in der Oberfläche die
 * Beschriftung.
 *
 * - **Wer**: `subject_id` ist die Person, um die es geht — ich selbst
 *   (privat, ohne Haushalt) oder ein Kind des Haushalts.
 * - **Inhalt**: alles in `kind`, `fields` und `plan` ist Ende-zu-Ende
 *   verschlüsselt und geht nie als Klartext hinaus (`ALWAYS_SEALED_TABLES`).
 * - **Fristen**: Ein Datumsfeld kann eine Frist sein („nächste Impfung
 *   fällig“, „läuft ab“). Daraus entsteht auf Wunsch eine Aufgabe oder ein
 *   Termin über die vorhandenen Schreibfunktionen — einen eigenen
 *   Erinnerungsmotor gibt es nicht. Die Akte merkt sich den Verweis (`plan`).
 *
 * Keine Körperzahlen (Gewicht, Körpergröße) — für Kinder nie (CLAUDE.md), und
 * die Akte ist nicht der Ort dafür. Medikamente tragen nur, was die Familie
 * selbst einträgt: Die App empfiehlt keine Dosis.
 */
import { isIsoDate } from './time.js';

export const RECORD_KINDS = [
  'doctor',
  'vaccination',
  'checkup',
  'allergy',
  'medication',
  'size',
  'school',
  'emergency',
  'insurance',
  'document',
] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

export function isRecordKind(value: unknown): value is RecordKind {
  return typeof value === 'string' && (RECORD_KINDS as readonly string[]).includes(value);
}

export type RecordFieldType = 'text' | 'multiline' | 'phone' | 'date';

export interface RecordField {
  key: string;
  type: RecordFieldType;
  /** Der Name des Eintrags in der Liste — Pflicht. */
  primary?: true;
  /** Ein Datum, das eine Frist ist — daraus wird auf Wunsch Aufgabe oder Termin. */
  due?: true;
}

/** Felder je Art, in der Reihenfolge des Formulars. Das erste ist immer der Name. */
export const RECORD_FIELDS = {
  doctor: [
    { key: 'name', type: 'text', primary: true },
    { key: 'specialty', type: 'text' },
    { key: 'phone', type: 'phone' },
    { key: 'address', type: 'multiline' },
  ],
  vaccination: [
    { key: 'vaccine', type: 'text', primary: true },
    { key: 'date', type: 'date' },
    { key: 'nextDue', type: 'date', due: true },
    { key: 'note', type: 'multiline' },
  ],
  // Vorsorge, etwa die U-Untersuchungen.
  checkup: [
    { key: 'name', type: 'text', primary: true },
    { key: 'date', type: 'date' },
    { key: 'nextDue', type: 'date', due: true },
    { key: 'note', type: 'multiline' },
  ],
  allergy: [
    { key: 'substance', type: 'text', primary: true },
    { key: 'reaction', type: 'text' },
    { key: 'note', type: 'multiline' },
  ],
  // Nur, was die Familie einträgt — keine Dosierungsempfehlung der App.
  medication: [
    { key: 'name', type: 'text', primary: true },
    { key: 'note', type: 'multiline' },
  ],
  // Kleidung und Schuhe — keine Körpermaße.
  size: [
    { key: 'item', type: 'text', primary: true },
    { key: 'size', type: 'text' },
    { key: 'note', type: 'multiline' },
  ],
  school: [
    { key: 'name', type: 'text', primary: true },
    { key: 'group', type: 'text' },
    { key: 'teacher', type: 'text' },
    { key: 'phone', type: 'phone' },
    { key: 'address', type: 'multiline' },
  ],
  emergency: [
    { key: 'name', type: 'text', primary: true },
    { key: 'relation', type: 'text' },
    { key: 'phone', type: 'phone' },
  ],
  insurance: [
    { key: 'insurer', type: 'text', primary: true },
    { key: 'number', type: 'text' },
    { key: 'phone', type: 'phone' },
  ],
  // Ausweis, Reisepass, Führerschein …
  document: [
    { key: 'name', type: 'text', primary: true },
    { key: 'number', type: 'text' },
    { key: 'expiresOn', type: 'date', due: true },
  ],
} as const satisfies Record<RecordKind, readonly RecordField[]>;

/** Höchstlänge je Feldtyp. */
export const RECORD_FIELD_MAX: Record<RecordFieldType, number> = {
  text: 200,
  multiline: 2000,
  phone: 40,
  date: 10,
};

/** Wer eine Akte sieht. Privat (`me`) gibt es nur ohne Haushalt — die eigene Akte. */
export const RECORD_VISIBILITIES = ['me', 'adults', 'child'] as const;
export type RecordVisibility = (typeof RECORD_VISIBILITIES)[number];

/** Wo eine Frist in der Planung steht: Aufgabe, eigener oder geteilter Termin. */
export const RECORD_PLAN_KINDS = ['task', 'event', 'household_event'] as const;
export type RecordPlanKind = (typeof RECORD_PLAN_KINDS)[number];

export interface RecordPlan {
  kind: RecordPlanKind;
  id: string;
  /** Der Tag, für den sie eingetragen wurde — ändert sich die Frist, fällt es auf. */
  date: string;
}

export type RecordFields = Record<string, string>;

export function recordFields(kind: RecordKind): readonly RecordField[] {
  return RECORD_FIELDS[kind];
}

/**
 * Felder säubern und prüfen: nur bekannte Schlüssel, getrimmt, leere fallen
 * weg (fehlend ist „keine Angabe“, nicht ""), Daten als `YYYY-MM-DD`,
 * Längen begrenzt, der Name Pflicht. `null` heißt ungültig — abgewiesen,
 * nicht still gekürzt.
 */
export function cleanRecordFields(kind: RecordKind, input: unknown): RecordFields | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const raw = input as Record<string, unknown>;
  const known = new Set<string>(recordFields(kind).map((field) => field.key));
  if (Object.keys(raw).some((key) => !known.has(key))) return null;
  const out: RecordFields = {};
  for (const field of recordFields(kind)) {
    const value = raw[field.key];
    if (value === undefined || value === null) continue;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (trimmed.length > RECORD_FIELD_MAX[field.type]) return null;
    if (field.type === 'date' && !isIsoDate(trimmed)) return null;
    out[field.key] = trimmed;
  }
  const primary = recordFields(kind).find((field) => 'primary' in field);
  if (primary && !out[primary.key]) return null;
  return out;
}

/** Der Name eines Eintrags — das Pflichtfeld seiner Art. */
export function recordTitle(kind: RecordKind, fields: RecordFields | null | undefined): string {
  const primary = recordFields(kind).find((field) => 'primary' in field);
  return (primary && fields?.[primary.key]) || '';
}

/** Das Fristfeld einer Art, falls sie eins hat. */
export function recordDueField(kind: RecordKind): string | null {
  return recordFields(kind).find((field) => 'due' in field)?.key ?? null;
}

/** Die Frist eines Eintrags — `null`, wenn die Art keine hat oder sie fehlt. */
export function recordDue(
  kind: RecordKind,
  fields: RecordFields | null | undefined,
): string | null {
  const key = recordDueField(kind);
  const value = key ? fields?.[key] : undefined;
  return value && isIsoDate(value) ? value : null;
}

/** Ab wie vielen Tagen vorher eine Frist als „bald“ gilt. */
export const RECORD_DUE_SOON_DAYS = 30;

export type RecordDueState = 'overdue' | 'soon' | 'later';

/** Steht die Frist bevor? Gerechnet in Kalendertagen (`today` in der Zeitzone des Nutzers). */
export function recordDueState(due: string, today: string): RecordDueState {
  if (due < today) return 'overdue';
  const days = Math.round(
    (Date.parse(`${due}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 864e5,
  );
  return days <= RECORD_DUE_SOON_DAYS ? 'soon' : 'later';
}

/**
 * Steht die Frist schon so in der Planung? Ein Verweis auf einen anderen Tag
 * heißt: Die Frist hat sich geändert, der Eintrag in der Planung nicht.
 */
export function recordPlanCurrent(
  due: string | null,
  plan: RecordPlan | null | undefined,
): boolean {
  return Boolean(due && plan && plan.date === due);
}

/**
 * Einträge der Liste nach Art (in der Reihenfolge von `RECORD_KINDS`), darin
 * nach Name. Leere Arten fallen weg — die Liste sagt dann „Keine Angabe“.
 */
export function groupRecords<T extends { kind: string; fields: RecordFields | null }>(
  entries: readonly T[],
): { kind: RecordKind; entries: T[] }[] {
  return RECORD_KINDS.map((kind) => ({
    kind,
    entries: entries
      .filter((entry) => entry.kind === kind)
      .sort((a, b) => recordTitle(kind, a.fields).localeCompare(recordTitle(kind, b.fields))),
  })).filter((group) => group.entries.length > 0);
}

/** `tel:`-Adresse aus einer eingetragenen Nummer — nur Ziffern, `+` vorn. */
export function telHref(phone: string): string | null {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits.length < 3) return null;
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`;
}
